import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Collapse,
  Divider,
  Grid,
  IconButton,
  Link,
  Skeleton,
  Typography
} from '@mui/material'
import {
  ExpandMore,
  ExpandLess,
  TrendingUp,
  TrendingDown,
  OpenInNew
} from '@mui/icons-material'
import { useEffect, useState } from 'react'
import { Stock } from '../_types/sector-heaters.type'
import { StockInfo } from '../_types/stock-info.type'
import { StockService } from '../services/stock.service'
import { useError } from '../context/error'
import { useTickerDrawer } from '../context/ticker-drawer-context'
import { Formatters } from '../_helpers/formatter.helper'
import StockLogo from './imgs/stock-logo'
import MiniStockChart from './mini-stock-chart'

interface HeaterCardProps {
  stock: Stock
  sectorTitle: string
}

export default function HeaterCard({ stock, sectorTitle }: HeaterCardProps) {
  const { setError } = useError()
  const stockService = new StockService(setError)
  const { toggleDrawer, setTicker } = useTickerDrawer()

  const [expanded, setExpanded] = useState(false)
  const [descriptionExpanded, setDescriptionExpanded] = useState(false)
  const [logoUrl, setLogoUrl] = useState<string | null>(null)
  const [stockInfo, setStockInfo] = useState<StockInfo | null>(null)
  const [loadingInfo, setLoadingInfo] = useState(false)
  const [loadingLogo, setLoadingLogo] = useState(true)

  const gainLossPercent = stock.currentPX && stock.entryPX
    ? ((stock.currentPX - stock.entryPX) / stock.entryPX) * 100
    : 0
  const gainLossDollar = stock.currentPX && stock.entryPX && stock.shares
    ? (stock.currentPX - stock.entryPX) * stock.shares
    : 0
  const totalValue = stock.currentPX && stock.shares
    ? stock.currentPX * stock.shares
    : 0
  const isPositive = gainLossPercent >= 0

  useEffect(() => {
    fetchLogo()
  }, [stock.symbol])

  useEffect(() => {
    if (expanded && !stockInfo) {
      fetchStockInfo()
    }
  }, [expanded])

  const fetchLogo = async () => {
    setLoadingLogo(true)
    const res = await stockService.getStockLogo(stock.symbol)
    if (res?.url) {
      setLogoUrl(res.url)
    }
    setLoadingLogo(false)
  }

  const fetchStockInfo = async () => {
    setLoadingInfo(true)
    const res = await stockService.getStockInfo(stock.symbol)
    if (res?.stockInfo) {
      setStockInfo(res.stockInfo)
    }
    setLoadingInfo(false)
  }

  const handleViewMore = () => {
    setTicker(stock.symbol)
    toggleDrawer()
  }

  return (
    <Card
      sx={{
        transition: 'box-shadow 0.2s',
        '&:hover': {
          boxShadow: 4
        }
      }}
    >
      <CardContent>
        {/* Collapsed Header */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            cursor: 'pointer'
          }}
          onClick={() => setExpanded(!expanded)}
        >
          {/* Logo */}
          <Box sx={{ width: 40, height: 40, flexShrink: 0 }}>
            {loadingLogo ? (
              <Skeleton variant="rectangular" width={40} height={40} sx={{ borderRadius: 1 }} />
            ) : (
              <StockLogo
                src={logoUrl || ''}
                alt={stock.symbol}
                sx={{ width: 40, height: 40 }}
              />
            )}
          </Box>

          {/* Name & Ticker */}
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography
              variant="subtitle1"
              fontWeight={600}
              noWrap
              title={stock.name}
            >
              {stock.name}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="body2" color="text.secondary">
                {stock.symbol}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                ·
              </Typography>
              <Typography variant="body2" color="text.secondary" noWrap>
                {stock.shares != null ? `${Formatters.formatCommas(stock.shares)} shares` : 'No position'}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                ·
              </Typography>
              <Typography
                variant="body2"
                fontWeight={600}
                color={isPositive ? 'success.main' : 'error.main'}
                noWrap
              >
                {Formatters.formatPercentage(gainLossPercent)}
              </Typography>
            </Box>
          </Box>

          {/* Expand Icon */}
          <IconButton size="small" sx={{ flexShrink: 0 }}>
            {expanded ? <ExpandLess /> : <ExpandMore />}
          </IconButton>
        </Box>

        {/* Expanded Content */}
        <Collapse in={expanded}>
          <Box sx={{ mt: 2 }}>
            {loadingInfo ? (
              <Box>
                <Grid container spacing={1}>
                  {[...Array(6)].map((_, i) => (
                    <Grid item xs={6} key={i}>
                      <Skeleton height={40} />
                    </Grid>
                  ))}
                </Grid>
                <Skeleton variant="rectangular" height={200} sx={{ mt: 2, borderRadius: 1 }} />
              </Box>
            ) : stockInfo ? (
              <>
                {/* Your Position Stats */}
                <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ mb: 0.5, display: 'block' }}>
                  CARNIVORE'S POSITION
                </Typography>
                <Grid container spacing={1}>
                  <Grid item xs={6}>
                    <Box sx={{ p: 1, bgcolor: 'primary.main', color: 'primary.contrastText', borderRadius: 1 }}>
                      <Typography variant="caption" sx={{ opacity: 0.8 }}>
                        Quantity
                      </Typography>
                      <Typography variant="body2" fontWeight={600}>
                        {stock.shares != null ? Formatters.formatCommas(stock.shares) : '-'}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={6}>
                    <Box sx={{ p: 1, bgcolor: 'primary.main', color: 'primary.contrastText', borderRadius: 1 }}>
                      <Typography variant="caption" sx={{ opacity: 0.8 }}>
                        Entry Price
                      </Typography>
                      <Typography variant="body2" fontWeight={600}>
                        ${Formatters.formatMoney(stock.entryPX)}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={6}>
                    <Box sx={{ p: 1, bgcolor: 'primary.main', color: 'primary.contrastText', borderRadius: 1 }}>
                      <Typography variant="caption" sx={{ opacity: 0.8 }}>
                        Current Value
                      </Typography>
                      <Typography variant="body2" fontWeight={600}>
                        ${Formatters.formatMoney(totalValue)}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={6}>
                    <Box sx={{ p: 1, bgcolor: isPositive ? 'success.main' : 'error.main', color: 'white', borderRadius: 1 }}>
                      <Typography variant="caption" sx={{ opacity: 0.8 }}>
                        Gain/Loss
                      </Typography>
                      <Typography variant="body2" fontWeight={600}>
                        {isPositive ? '+' : ''}${Formatters.formatMoney(gainLossDollar)} ({Formatters.formatPercentage(gainLossPercent)})
                      </Typography>
                    </Box>
                  </Grid>
                </Grid>

                <Divider sx={{ my: 2 }} />

                {/* Market Stats */}
                <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ mb: 0.5, display: 'block' }}>
                  MARKET STATS
                </Typography>
                <Grid container spacing={1}>
                  <Grid item xs={6}>
                    <Box sx={{ p: 1, bgcolor: 'action.hover', borderRadius: 1 }}>
                      <Typography variant="caption" color="text.secondary">
                        Current Price
                      </Typography>
                      <Typography variant="body2" fontWeight={600}>
                        ${Formatters.formatMoney(Number(stockInfo.currentPrice))}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={6}>
                    <Box sx={{ p: 1, bgcolor: 'action.hover', borderRadius: 1 }}>
                      <Typography variant="caption" color="text.secondary">
                        % Change
                      </Typography>
                      <Typography
                        variant="body2"
                        fontWeight={600}
                        color={parseFloat(stockInfo.quote.percent_change) >= 0 ? 'success.main' : 'error.main'}
                      >
                        {stockInfo.quote.percent_change}%
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={6}>
                    <Box sx={{ p: 1, bgcolor: 'action.hover', borderRadius: 1 }}>
                      <Typography variant="caption" color="text.secondary">
                        Volume
                      </Typography>
                      <Typography variant="body2" fontWeight={600}>
                        {Formatters.formatCommas(Number(stockInfo.quote.volume))}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={6}>
                    <Box sx={{ p: 1, bgcolor: 'action.hover', borderRadius: 1 }}>
                      <Typography variant="caption" color="text.secondary">
                        52W High
                      </Typography>
                      <Typography variant="body2" fontWeight={600}>
                        ${stockInfo.quote.fifty_two_week?.high || 'N/A'}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={6}>
                    <Box sx={{ p: 1, bgcolor: 'action.hover', borderRadius: 1 }}>
                      <Typography variant="caption" color="text.secondary">
                        52W Low
                      </Typography>
                      <Typography variant="body2" fontWeight={600}>
                        ${stockInfo.quote.fifty_two_week?.low || 'N/A'}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={6}>
                    <Box sx={{ p: 1, bgcolor: 'action.hover', borderRadius: 1 }}>
                      <Typography variant="caption" color="text.secondary">
                        Sector
                      </Typography>
                      <Typography variant="body2" fontWeight={600} noWrap title={stockInfo.profile?.sector}>
                        {stockInfo.profile?.sector || 'N/A'}
                      </Typography>
                    </Box>
                  </Grid>
                </Grid>

                {/* Description */}
                {stockInfo.profile?.description && (
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ mb: 0.5, display: 'block' }}>
                      ABOUT
                    </Typography>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{
                        display: '-webkit-box',
                        WebkitLineClamp: descriptionExpanded ? 'unset' : 3,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden'
                      }}
                    >
                      {stockInfo.profile.description}
                    </Typography>
                    <Link
                      component="button"
                      variant="body2"
                      onClick={() => setDescriptionExpanded(!descriptionExpanded)}
                      sx={{ mt: 0.5 }}
                    >
                      {descriptionExpanded ? 'See less' : 'See more'}
                    </Link>
                  </Box>
                )}

                {/* Mini Chart */}
                <Box sx={{ mt: 2 }}>
                  <MiniStockChart ticker={stock.symbol} />
                </Box>

                {/* View More Button */}
                <Box sx={{ mt: 2, textAlign: 'center' }}>
                  <Button
                    variant="outlined"
                    size="small"
                    endIcon={<OpenInNew />}
                    onClick={handleViewMore}
                  >
                    View More
                  </Button>
                </Box>
              </>
            ) : (
              <Typography variant="body2" color="text.secondary" textAlign="center" sx={{ py: 2 }}>
                Unable to load stock information
              </Typography>
            )}
          </Box>
        </Collapse>
      </CardContent>
    </Card>
  )
}
